import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import { useMapManager } from "../hooks/useMapManager";

import { Modal, Descriptions, Row, Col, Tabs, Timeline, Spin } from "antd";

import TrashIcon from "../../../../../components/utils/icons/trash";
import ButtonStandard from "../../../../../components/utils/button";
import AnimalInfoDisplay from "./AnimalInfoDisplay";
import LotHistory from "../../../../../components/utils/lotHistory";

// Services
import { getMockAreaData } from "../mocks/selectedAreaDataMock";
import { logEventError, logEventInfo } from "../../../../../utils/logger";
import { getFarmMapsAnimalData } from "../../../../../services/mapService";
import {
  getPicketComments,
  getPicketLotHistories,
} from "../../../../../services/picketService";

const { TabPane } = Tabs;

const AreaInfoModal = ({ onClose, translation, onEditArea, onDeleteArea }) => {
  const { modalVisible, selectedAreaData } = useMapManager();
  const [animalData, setAnimalData] = useState(null);
  const [lotHistories, setLotHistories] = useState([]);
  const [observations, setObservations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Mock dos dados da área selecionada - estrutura do back-end
  const hasPicketInfo =
    selectedAreaData?.picketId && selectedAreaData?.picketName ? true : false;

  const mockAreaData = getMockAreaData(
    selectedAreaData?.picketProductionSubSystem || "Extensive"
  );

  const handleGetAnimalData = useCallback(async () => {
    try {
      const { groupId, farmId, id } = selectedAreaData;
      const {
        data: { results: response },
      } = await getFarmMapsAnimalData({
        groupId,
        farmId,
        id,
        signal: AbortSignal.timeout(10000),
      });
      logEventInfo("Dados dos animais buscados com sucesso:", response);
      setAnimalData(response);
    } catch (error) {
      logEventError("Erro ao buscar dados dos animais:", error);
    }
  }, [selectedAreaData]);

  const handleGetLotHistoriesData = useCallback(async () => {
    const { groupId, farmId, id, picketId } = selectedAreaData;
    if (picketId) {
      try {
        const {
          data: { results: response },
        } = await getPicketLotHistories({
          groupId,
          farmId,
          id: picketId,
          signal: AbortSignal.timeout(10000),
        });
        logEventInfo("Dados dos históricos buscados	com sucesso:", response);
        setLotHistories(response);
      } catch (error) {
        logEventError("Erro ao buscar dados históricos:", error);
      }
    }
  }, [selectedAreaData]);

  const handleGetObservationsData = useCallback(async () => {
    try {
      const { groupId, farmId, picketId } = selectedAreaData;
      if (picketId) {
        const {
          data: { results: response },
        } = await getPicketComments({
          groupId,
          farmId,
          id: picketId,
          signal: AbortSignal.timeout(10000),
        });
        logEventInfo("Dados dos observações buscados	com sucesso:", response);
        setObservations(response);
      }
    } catch (error) {
      logEventError("Erro ao buscar dados observações:", error);
    }
  }, [selectedAreaData]);

  const handleEditArea = () => {
    if (onEditArea) {
      onEditArea(selectedAreaData);
      onClose();
    }
  };

  const handleDeleteArea = () => {
    Modal.confirm({
      title:
        translation.areaMap.modal.deleteConfirmTitle || "Confirmar Exclusão",
      content:
        translation.areaMap.modal.deleteConfirmMessage ||
        "Tem certeza que deseja excluir esta área? Esta ação não pode ser desfeita.",
      okText: translation.areaMap.modal.deleteConfirmOk || "Sim, excluir",
      cancelText: translation.areaMap.modal.deleteConfirmCancel || "Cancelar",
      okType: "danger",
      onOk: async () => {
        if (onDeleteArea) {
          await onDeleteArea(selectedAreaData?.id);
        }
        onClose();
      },
    });
  };

  const getStatusText = (status, productionSubSystem) => {
    const statusMap = {
      B: translation.areaMap.modal.status.B, // Break
      P:
        productionSubSystem === "Intensive"
          ? translation.areaMap.modal.status.A
          : translation.areaMap.modal.status.P, // Pasturage
      R: translation.areaMap.modal.status.R, // Renovation
      M: translation.areaMap.modal.status.M, // Maintenance
      L: translation.areaMap.modal.status.L, // Leased
      D: translation.areaMap.modal.status.D, // Deleted
      X: translation.areaMap.modal.status.X, // Farm Excluded
      I: translation.areaMap.modal.status.I, // Inactive
      Break: translation.areaMap.modal.status.B, // Break
      Pasturage:
        productionSubSystem === "Intensive"
          ? translation.areaMap.modal.status.A
          : translation.areaMap.modal.status.P, // Pasturage
      Renovation: translation.areaMap.modal.status.R, // Renovation
      Maintenance: translation.areaMap.modal.status.M, // Maintenance
      Leased: translation.areaMap.modal.status.L, // Leased
      Deleted: translation.areaMap.modal.status.D, // Deleted
      Inactive: translation.areaMap.modal.status.I, // Inactive
    };
    return statusMap[status] || status;
  };

  const getActivityText = (activity) => {
    const activityMap = {
      Extensive: translation.areaMap.modal.picketProductionSubSystem.extensive,
      SemiIntensive:
        translation.areaMap.modal.picketProductionSubSystem.semiIntensive,
      Intensive: translation.areaMap.modal.picketProductionSubSystem.intensive,
    };
    return activityMap[activity] || activity;
  };

  const renderDetailsTab = () => (
    <Descriptions column={1} bordered size="small">
      {selectedAreaData?.picketStatus && (
        <Descriptions.Item label={translation.areaMap.modal.details.status}>
          {selectedAreaData.picketStatusDays} dias -{" "}
          {getStatusText(
            selectedAreaData.picketStatus,
            selectedAreaData.picketProductionSubSystem
          )}
        </Descriptions.Item>
      )}
      {selectedAreaData?.picketProductionSubSystem && (
        <Descriptions.Item
          label={translation.areaMap.modal.details.picketProductionSubSystem}
        >
          {getActivityText(
            selectedAreaData.picketProductionSubSystem || "Extensive"
          )}
        </Descriptions.Item>
      )}
      {selectedAreaData?.identificationCode && (
        <Descriptions.Item
          label={translation.areaMap.modal.details.identificationCode}
        >
          {selectedAreaData?.identificationCode}
        </Descriptions.Item>
      )}
      {selectedAreaData?.type && (
        <Descriptions.Item label={translation.areaMap.modal.details.type}>
          {translation.areaMap.types[selectedAreaData?.type] || "-"}
        </Descriptions.Item>
      )}
      {selectedAreaData?.picketName && (
        <Descriptions.Item label={translation.areaMap.modal.details.picketName}>
          {selectedAreaData?.picketName}
        </Descriptions.Item>
      )}
      <Descriptions.Item
        label={translation.areaMap.modal.details.areaSquareMeters}
      >
        {(selectedAreaData?.areaInSquareMeters || 0).toLocaleString("pt-BR")} m²
      </Descriptions.Item>
      {(selectedAreaData?.picketProductionSubSystem === "Extensive" ||
        selectedAreaData?.picketProductionSubSystem === "SemiIntensive") && (
        <Descriptions.Item
          label={translation.areaMap.modal.details.areaHectares}
        >
          {(selectedAreaData?.areaInHectares || 0).toLocaleString("pt-BR")} ha
        </Descriptions.Item>
      )}
      {selectedAreaData?.picketPastureName && (
        <Descriptions.Item
          label={translation.areaMap.modal.details.picketPastureName}
        >
          {selectedAreaData.picketPastureName}
        </Descriptions.Item>
      )}
      {selectedAreaData?.observation && (
        <Descriptions.Item
          label={translation.areaMap.modal.details.areaObservations}
        >
          {selectedAreaData?.observation}
        </Descriptions.Item>
      )}
    </Descriptions>
  );

  const renderProductionTab = () => (
    <AnimalInfoDisplay
      animalData={animalData}
      translation={translation}
      showStockingRate={
        selectedAreaData?.picketProductionSubSystem === "Extensive" ||
        selectedAreaData?.picketProductionSubSystem === "SemiIntensive"
      }
    />
  );

  const renderHistoryTab = () => <LotHistory lotHistories={lotHistories} />;

  const renderObservationsTab = useCallback(() => {
    if (observations.length > 0) {
      return (
        <div>
          <Timeline>
            {observations.map((obs) => (
              <Timeline.Item key={obs.id}>
                <span>
                  <strong>
                    {moment(obs.commentDate).format("DD/MM/YYYY")}
                  </strong>{" "}
                  - {obs.comment}
                </span>
              </Timeline.Item>
            ))}
          </Timeline>
        </div>
      );
    } else {
      return null;
    }
  }, [observations, translation]);

  // Mock data - em produção, estes dados viriam de APIs
  useEffect(() => {
    function loadData() {
      Promise.all([
        handleGetAnimalData(),
        handleGetLotHistoriesData(),
        handleGetObservationsData(),
      ]).then(() => {
        setIsLoading(false);
      });
    }
    if (modalVisible) {
      setIsLoading(true);
      loadData();
    }
  }, [modalVisible]);

  return (
    <Modal
      title={translation.areaMap.modal.title || "Informações da Área"}
      visible={modalVisible}
      onCancel={() => onClose()}
      footer={null}
      width={800}
      centered
    >
      <Spin spinning={isLoading}>
        <Tabs defaultActiveKey="details">
          <TabPane tab={translation.areaMap.modal.tabs.details} key="details">
            {renderDetailsTab()}
          </TabPane>
          {hasPicketInfo && (
            <TabPane
              tab={translation.areaMap.modal.tabs.production}
              key="production"
            >
              {renderProductionTab()}
            </TabPane>
          )}
          {hasPicketInfo && (
            <TabPane tab={translation.areaMap.modal.tabs.history} key="history">
              {renderHistoryTab()}
            </TabPane>
          )}
          {hasPicketInfo && (
            <TabPane
              tab={translation.areaMap.modal.tabs.observations}
              key="observations"
            >
              {renderObservationsTab()}
            </TabPane>
          )}
        </Tabs>

        <Row
          type="flex"
          gutter={[8, 8]}
          justify="end"
          style={{ marginTop: "16px" }}
        >
          <Col>
            <ButtonStandard
              type="button"
              buttonType="secondary"
              onClick={handleEditArea}
            >
              {translation.buttons.edit}
            </ButtonStandard>
          </Col>
          <Col>
            <ButtonStandard
              type="button"
              buttonType="type7"
              onClick={handleDeleteArea}
            >
              <TrashIcon />{" "}
              {translation.areaMap.modal.deleteButton || "Excluir Área"}
            </ButtonStandard>
          </Col>
        </Row>
      </Spin>
    </Modal>
  );
};

export default AreaInfoModal;
